import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { useAtlasAuthContext } from "@/contexts/atlas-auth";
import { Uuid } from "@/domains/global/identifiers";
import { toastModule } from "@/modules/toast";
import {
  AudioRecordingInternalResponseSchema,
  finalizeAudioRecordingUpload,
  fetchAudioRecordingInternal,
  registerAudioRecordingUpload,
} from "@/pages/audio-recordings/shared/api";
import { uploadSingleFile } from "@/pages/audio-recordings/recorder/upload-single-file";

const DEFAULT_MIME_TYPE = "audio/webm";

type RecordingState = "idle" | "recording" | "paused";
type UploadState = "idle" | "uploading" | "completed" | "error";

interface RegisteredSession {
  bucketName: string;
  spaceAccountId: string;
  normalizedFilename: string;
}

const formatBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(1)} ${units[index]}`;
};

const Container = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.section`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Input = styled.input`
  border: 1px solid #cbd5f5;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
`;

const Text = styled.p`
  margin: 0;
  color: #4a5568;
  line-height: 1.5;
`;

const StatusBadge = styled.span<{ tone: "idle" | "active" | "success" | "error" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 4px 12px;
  font-weight: 600;
  color: ${({ tone }) =>
    tone === "success"
      ? "#0a5f38"
      : tone === "error"
        ? "#8b1e1e"
        : tone === "active"
          ? "#0b5394"
          : "#4a5568"};
  background-color: ${({ tone }) =>
    tone === "success"
      ? "#d6f2e3"
      : tone === "error"
        ? "#f8d7da"
        : tone === "active"
          ? "#d9e8fb"
          : "#f2f2f2"};
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 12px;
  background: #f0f0f0;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div<{ percentage: number }>`
  height: 100%;
  background: linear-gradient(90deg, #1166dd, #33a3ff);
  width: ${({ percentage }) => `${percentage}%`};
  transition: width 160ms ease-out;
`;

const List = styled.ol`
  margin: 0;
  padding-left: 20px;
  color: #4a5568;
  line-height: 1.6;
`;

export const AudioRecordingsRecorderPage = () => {
  const navigate = useNavigate();
  const { audioRecordingId } = useParams<{ audioRecordingId: Uuid }>();
  const {
    authContext: { authToken },
  } = useAtlasAuthContext();

  const [filename, setFilename] = useState(
    () => `audio-recording-${audioRecordingId ?? "new"}.webm`
  );
  const [mimeType, setMimeType] = useState(DEFAULT_MIME_TYPE);
  const [isRegistering, setIsRegistering] = useState(false);
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [uploadProgress, setUploadProgress] = useState({ uploaded: 0, total: 0 });
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [session, setSession] = useState<RegisteredSession | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  const recordingQuery = useQuery({
    queryKey: ["audio-recording-internal", audioRecordingId],
    queryFn: () => (audioRecordingId ? fetchAudioRecordingInternal(audioRecordingId) : null),
    enabled: Boolean(audioRecordingId),
    staleTime: 30 * 1000,
  });

  const recording: AudioRecordingInternalResponseSchema | null = recordingQuery.data ?? null;
  const mediaSupported = typeof window !== "undefined" && "MediaRecorder" in window;

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, []);

  const handleRegister = useCallback(async () => {
    if (!audioRecordingId) return;

    setIsRegistering(true);
    setErrorMessage(null);
    try {
      const response = await registerAudioRecordingUpload(audioRecordingId, {
        filename,
        mime_type: mimeType,
      });
      if (!response) {
        throw new Error("Missing register response");
      }

      setSession({
        bucketName: response.bucket_name,
        spaceAccountId: response.space_account_id,
        normalizedFilename: response.normalized_filename,
      });
      toastModule.triggerToast({ content: "Recording session registered." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to register.";
      setErrorMessage(message);
      toastModule.triggerToast({ content: message });
    } finally {
      setIsRegistering(false);
    }
  }, [audioRecordingId, filename, mimeType]);

  const setupMediaStream = useCallback(async () => {
    if (!mediaSupported) {
      toastModule.triggerToast({ content: "MediaRecorder not supported in this browser." });
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;
  }, [mediaSupported]);

  const handleStartRecording = async () => {
    if (!session) {
      toastModule.triggerToast({ content: "Register the upload before recording." });
      return;
    }

    try {
      if (!mediaStreamRef.current) {
        await setupMediaStream();
      }

      if (!mediaStreamRef.current) {
        throw new Error("Failed to access microphone");
      }

      recordedChunksRef.current = [];
      setRecordedBlob(null);

      const recorder = new MediaRecorder(mediaStreamRef.current, { mimeType });

      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setRecordingState("idle");
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecordingState("recording");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to access microphone.";
      toastModule.triggerToast({ content: message });
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const handlePauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setRecordingState("paused");
    }
  };

  const handleResumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setRecordingState("recording");
    }
  };

  const handleUpload = async () => {
    if (!session || !recordedBlob || !audioRecordingId || !authToken) {
      toastModule.triggerToast({ content: "No recording to upload or missing auth." });
      return;
    }

    setUploadState("uploading");
    setUploadProgress({ uploaded: 0, total: recordedBlob.size });
    setErrorMessage(null);

    const result = await uploadSingleFile({
      file: recordedBlob,
      mimeType,
      filename: session.normalizedFilename,
      spaceAccountId: session.spaceAccountId,
      audioRecordingId,
      bucketName: session.bucketName,
      authToken,
      onProgress: (uploaded, total) => {
        setUploadProgress({ uploaded, total });
      },
    });

    if (result.success) {
      setUploadState("completed");
      toastModule.triggerToast({ content: "Upload completed!" });
    } else {
      setUploadState("error");
      setErrorMessage(result.error ?? "Upload failed");
      toastModule.triggerToast({ content: result.error ?? "Upload failed" });
    }
  };

  const handleFinalize = async () => {
    if (!audioRecordingId || !session) return;

    setIsFinalizing(true);
    setErrorMessage(null);
    try {
      await finalizeAudioRecordingUpload(audioRecordingId, {
        filename: session.normalizedFilename,
        mime_type: mimeType,
      });

      toastModule.triggerToast({ content: "Recording finalized!" });
      recordingQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to finalize.";
      setErrorMessage(message);
      toastModule.triggerToast({ content: message });
    } finally {
      setIsFinalizing(false);
    }
  };

  const progressPercentage = useMemo(() => {
    if (uploadProgress.total === 0) return 0;
    return Math.round((uploadProgress.uploaded / uploadProgress.total) * 100);
  }, [uploadProgress]);

  if (!audioRecordingId) {
    return <Container>Missing audio recording ID.</Container>;
  }

  return (
    <Container>
      <div>
        <h1>Audio Recording Recorder</h1>
        <Text>Recording ID: {audioRecordingId}</Text>
        {recording && <Text>Space Account: {recording.owned_by_space_account_id}</Text>}
      </div>

      <Section>
        <SectionTitle>1. Register upload</SectionTitle>
        <Text>Reserve the CapturedAudio row and get upload configuration.</Text>
        <FieldRow>
          <Input value={filename} onChange={event => setFilename(event.target.value)} />
          <Input value={mimeType} onChange={event => setMimeType(event.target.value)} />
          <Button type="button" onClick={handleRegister} disabled={isRegistering}>
            {isRegistering ? "Registering…" : "Register"}
          </Button>
        </FieldRow>
        {session && (
          <Text>
            Bucket: <strong>{session.bucketName}</strong> · File:{" "}
            <strong>{session.normalizedFilename}</strong>
          </Text>
        )}
      </Section>

      <Section>
        <SectionTitle>2. Record audio</SectionTitle>
        <Text>Record audio and stop when done. The recording will be saved as a single file.</Text>
        <FieldRow>
          <Button
            type="button"
            onClick={handleStartRecording}
            disabled={!session || recordingState !== "idle"}
          >
            Start Recording
          </Button>
          <Button
            type="button"
            onClick={handlePauseRecording}
            disabled={recordingState !== "recording"}
          >
            Pause
          </Button>
          <Button
            type="button"
            onClick={handleResumeRecording}
            disabled={recordingState !== "paused"}
          >
            Resume
          </Button>
          <Button type="button" onClick={handleStopRecording} disabled={recordingState === "idle"}>
            Stop Recording
          </Button>
        </FieldRow>
        <StatusBadge
          tone={
            recordingState === "recording"
              ? "active"
              : recordingState === "paused"
                ? "idle"
                : "idle"
          }
        >
          Recorder status: {recordingState}
        </StatusBadge>
        {recordedBlob && (
          <Text>
            Recorded: <strong>{formatBytes(recordedBlob.size)}</strong>
          </Text>
        )}
      </Section>

      <Section>
        <SectionTitle>3. Upload</SectionTitle>
        <Text>Upload the recorded audio via TUS protocol (resumable).</Text>
        <FieldRow>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!recordedBlob || uploadState === "uploading" || uploadState === "completed"}
          >
            {uploadState === "uploading" ? "Uploading…" : "Upload"}
          </Button>
        </FieldRow>
        <StatusBadge
          tone={
            uploadState === "error"
              ? "error"
              : uploadState === "uploading"
                ? "active"
                : uploadState === "completed"
                  ? "success"
                  : "idle"
          }
        >
          Upload status: {uploadState}
        </StatusBadge>
        {uploadState === "uploading" && (
          <>
            <ProgressBarOuter>
              <ProgressBarInner percentage={progressPercentage} />
            </ProgressBarOuter>
            <Text>
              Uploaded {formatBytes(uploadProgress.uploaded)} / {formatBytes(uploadProgress.total)}{" "}
              ({progressPercentage}%)
            </Text>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>4. Finalize</SectionTitle>
        <Text>Finalize the upload to mark the recording as complete.</Text>
        <FieldRow>
          <Button
            type="button"
            onClick={handleFinalize}
            disabled={isFinalizing || uploadState !== "completed"}
          >
            {isFinalizing ? "Finalizing…" : "Finalize"}
          </Button>
          <Button
            type="button"
            variant="soft"
            onClick={() =>
              navigate(atlasRoutes.audioRecordingsView({ params: { audioRecordingId } }).path)
            }
          >
            View Details
          </Button>
        </FieldRow>
      </Section>

      {errorMessage && (
        <Section>
          <Text style={{ color: "#8b1e1e" }}>Error: {errorMessage}</Text>
        </Section>
      )}

      <Section>
        <SectionTitle>How it works</SectionTitle>
        <List>
          <li>Register to reserve the CapturedAudio row and get bucket info.</li>
          <li>Record audio using the browser&apos;s MediaRecorder API.</li>
          <li>Upload the complete recording via TUS (resumable upload protocol).</li>
          <li>Finalize to mark the recording as complete in the backend.</li>
        </List>
      </Section>
    </Container>
  );
};
